import {
  InsurancePolicy,
  InsurancePolicyType,
} from '@advisory-report/src/types/AdvisoryReport';
import { UseQueryOptions } from 'react-query';
import {
  getDocumentMetadata,
  SchemaSingularityJobQueuedResponse,
} from 'wg-api-sdk';
import { wgApiSdk, wgApi } from '../../../../../../app/src/features/api/wgApi';
import {
  useSingularityJobBasedFetch,
  SingularityJobResultResponse,
} from '@common/src/hooks/useSingularityJobBasedFetch';

interface Params {
  documentId?: string;
  /** IMPORTANT: Make sure the filename is decoded */
  filename?: string;
  type: InsurancePolicyType;
}

interface InsurancePolicyExtractionData {
  items: InsurancePolicy[];
}

interface EnqueueParams {
  documentId: string;
  filename?: string;
  type: InsurancePolicyType;
}

export const postInsurancePolicyExtractionEnqueue = async (
  document_id: string,
  filename?: string,
  type?: InsurancePolicyType,
) => {
  const doc = await getDocumentMetadata({ path: { document_id } }, wgApiSdk);

  if (!doc?.link) {
    throw new Error('Document link not found');
  }

  const insurancesPath = `/v2/api/document-extractor/one/insurance-policies`;
  const offersPath = `/v2/api/document-extractor/one/offers`;

  const path = type === 'OFFER' ? offersPath : insurancesPath;
  const { data, isError } =
    await wgApi.post<SchemaSingularityJobQueuedResponse>(path, {
      link: doc.link,
      document_id: document_id,
      filename: filename || '',
    });

  if (isError || !data) {
    throw new Error('Error enqueueing extraction job');
  }

  return data;
};

export const getUseInsurancePolicyExtractionEnqueueQKey = (
  document_id?: string,
  type?: string,
) => ['insurance-policy-extraction-enqueue', type, document_id];

export const getUseInsurancePolicyExtractionResultQKey = (job_id?: string) => [
  'insurance-policy-extraction-result',
  job_id,
];

export const useInsurancePolicyExtraction = (
  params?: Params,
  queryOptions?: UseQueryOptions<
    SingularityJobResultResponse<InsurancePolicyExtractionData>,
    unknown,
    SingularityJobResultResponse<InsurancePolicyExtractionData>
  >,
) => {
  const { documentId, filename, type } = params || {};

  return useSingularityJobBasedFetch<
    InsurancePolicyExtractionData,
    EnqueueParams
  >({
    enqueueFn: ({ documentId, filename, type }) =>
      postInsurancePolicyExtractionEnqueue(documentId, filename, type),
    enqueueQueryKey: ({ documentId, type }) =>
      getUseInsurancePolicyExtractionEnqueueQKey(documentId, type),
    resultQueryKey: getUseInsurancePolicyExtractionResultQKey,
    enqueueParams:
      documentId && type ? { documentId, filename, type } : undefined,
    queryOptions,
  });
};
